import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { hpcStore } from '../stores/HPCStore'
import Card from '../components/common/Card'
import Button from '../components/common/Button'
import Badge from '../components/common/Badge'
import Pagination from '../components/common/Pagination'
import { WebTerminal } from '../components/hpc/WebTerminal'
import { HPCJobStatus, HPCJobType, ClusterStatus } from '../types'
import { apiClient } from '../services/api'
import toast from 'react-hot-toast'
import {
  ClipboardDocumentIcon,
  CommandLineIcon,
  CpuChipIcon,
  XMarkIcon,
  PlusIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline'

// Types for interactive sessions
interface InteractiveConfig {
  name: string
  description: string
  nodes: number
  cpus_per_node: number
  memory_gb: number
  gpus: number
  gpu_type: string | null
  walltime_hours: number
}

interface GPUInfo {
  type: string
  vram_gb: number
  description: string
  availability: string
}

const HPCJobsPage = observer(() => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'jobs' | 'clusters' | 'interactive'>('jobs')
  const [statusFilter, setStatusFilter] = useState<HPCJobStatus | ''>('')

  // Interactive session state
  const [interactiveConfigs, setInteractiveConfigs] = useState<InteractiveConfig[]>([])
  const [gpuTypes, setGpuTypes] = useState<GPUInfo[]>([])
  const [generatedCommand, setGeneratedCommand] = useState<string>('')
  const [commandInstructions, setCommandInstructions] = useState<string[]>([])
  const [customConfig, setCustomConfig] = useState({
    nodes: 1,
    cpus: 8,
    memory_gb: 32,
    gpus: 0,
    gpu_type: '',
    walltime_hours: 4,
    queue: '',
  })

  // Web terminal state
  const [showTerminal, setShowTerminal] = useState(false)
  const [terminalConfig, setTerminalConfig] = useState({
    hostname: 'login.cx3.hpc.ic.ac.uk',
    username: '',
    initialCommand: '',
  })

  // Cluster creation modal state
  const [showClusterModal, setShowClusterModal] = useState(false)
  const [newCluster, setNewCluster] = useState({
    name: '',
    description: '',
    hostname: '',
    port: 22,
    scheduler_type: 'pbs',
    ssh_username: '',
    max_concurrent_jobs: 10,
    default_queue: 'short',
    available_queues: ['short', 'medium', 'long', 'gpu'],
    gpu_types: ['L40S', 'RTX6000', 'A100', 'V100'],
    work_directory: '',
  })
  const [creatingCluster, setCreatingCluster] = useState(false)

  useEffect(() => {
    hpcStore.fetchClusters()
    hpcStore.fetchJobs()
  }, [])

  // Fetch interactive session configs and GPU types when switching to interactive tab
  useEffect(() => {
    if (activeTab === 'interactive') {
      fetchInteractiveData()
    }
  }, [activeTab])

  const fetchInteractiveData = async () => {
    try {
      const [configsRes, gpuRes] = await Promise.all([
        apiClient.get<InteractiveConfig[]>('/api/v1/hpc/interactive/configs'),
        apiClient.get<{ gpu_details: GPUInfo[] }>('/api/v1/hpc/gpu-types'),
      ])
      setInteractiveConfigs(configsRes.data)
      setGpuTypes(gpuRes.data.gpu_details)
    } catch (error) {
      console.error('Failed to fetch interactive data:', error)
    }
  }

  const generateInteractiveCommand = async (config?: InteractiveConfig) => {
    try {
      const params = config ? {
        nodes: config.nodes,
        cpus: config.cpus_per_node,
        memory_gb: config.memory_gb,
        gpus: config.gpus,
        gpu_type: config.gpu_type || undefined,
        walltime_hours: config.walltime_hours,
      } : {
        nodes: customConfig.nodes,
        cpus: customConfig.cpus,
        memory_gb: customConfig.memory_gb,
        gpus: customConfig.gpus,
        gpu_type: customConfig.gpu_type || undefined,
        walltime_hours: customConfig.walltime_hours,
        queue: customConfig.queue || undefined,
      }

      const res = await apiClient.post<{
        command: string
        instructions: string[]
        tips: string[]
      }>('/api/v1/hpc/interactive/command', null, { params })

      setGeneratedCommand(res.data.command)
      setCommandInstructions([...res.data.instructions, ...res.data.tips])
      toast.success('Command generated!')
    } catch (error) {
      console.error('Failed to generate command:', error)
      toast.error('Failed to generate command')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!')
  }

  // Start web terminal session
  const startTerminalSession = (command?: string) => {
    // Get username from first configured cluster or from newCluster form
    const cluster = hpcStore.clusters.find(c =>
      c.hostname.includes('cx3') || c.hostname.includes('imperial')
    )
    const username = cluster?.ssh_username || newCluster.ssh_username || ''

    setTerminalConfig({
      hostname: cluster?.hostname || 'login.cx3.hpc.ic.ac.uk',
      username: username,
      initialCommand: command || generatedCommand,
    })
    setShowTerminal(true)
  }

  // Create new HPC cluster
  const handleCreateCluster = async () => {
    if (!newCluster.name || !newCluster.hostname || !newCluster.ssh_username) {
      toast.error('Please fill in all required fields')
      return
    }

    setCreatingCluster(true)
    try {
      await apiClient.post('/api/v1/hpc/clusters', newCluster)
      toast.success('Cluster created successfully!')
      setShowClusterModal(false)
      // Reset form
      setNewCluster({
        name: '',
        description: '',
        hostname: '',
        port: 22,
        scheduler_type: 'pbs',
        ssh_username: '',
        max_concurrent_jobs: 10,
        default_queue: 'short',
        available_queues: ['short', 'medium', 'long', 'gpu'],
        gpu_types: ['L40S', 'RTX6000', 'A100', 'V100'],
        work_directory: '',
      })
      // Refresh clusters
      hpcStore.fetchClusters()
    } catch (error: any) {
      console.error('Failed to create cluster:', error)
      toast.error(error.response?.data?.detail || 'Failed to create cluster')
    } finally {
      setCreatingCluster(false)
    }
  }

  const handleStatusFilterChange = (status: HPCJobStatus | '') => {
    setStatusFilter(status)
    hpcStore.fetchJobs({ status: status || undefined })
  }

  const handlePageChange = (page: number) => {
    hpcStore.fetchJobs({ page, status: statusFilter || undefined })
  }

  const getStatusBadgeVariant = (status: HPCJobStatus): 'gray' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case HPCJobStatus.COMPLETED:
        return 'success'
      case HPCJobStatus.RUNNING:
      case HPCJobStatus.CHECKPOINTING:
        return 'warning'
      case HPCJobStatus.FAILED:
      case HPCJobStatus.CANCELLED:
        return 'error'
      default:
        return 'gray'
    }
  }

  const getClusterStatusBadgeVariant = (status: ClusterStatus): 'gray' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case ClusterStatus.ACTIVE:
        return 'success'
      case ClusterStatus.MAINTENANCE:
        return 'warning'
      case ClusterStatus.ERROR:
        return 'error'
      default:
        return 'gray'
    }
  }

  const getJobTypeLabel = (type: HPCJobType): string => {
    switch (type) {
      case HPCJobType.RL_TRAINING:
        return 'RL Training'
      case HPCJobType.LLM_FINETUNING:
        return 'LLM Fine-tuning'
      case HPCJobType.MULTI_AGENT_SIMULATION:
        return 'Multi-Agent Sim'
      case HPCJobType.BATCH_INFERENCE:
        return 'Batch Inference'
      case HPCJobType.CUSTOM:
        return 'Custom'
      default:
        return type
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            HPC Computing
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Manage HPC clusters and training jobs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowClusterModal(true)}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Cluster
          </Button>
          <Button size="sm" onClick={() => navigate('/hpc/jobs/new')}>
            New Job
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'jobs'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            Jobs ({hpcStore.jobsTotal})
          </button>
          <button
            onClick={() => setActiveTab('clusters')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'clusters'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            Clusters ({hpcStore.clusters.length})
          </button>
          <button
            onClick={() => setActiveTab('interactive')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'interactive'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            Interactive Sessions
          </button>
        </nav>
      </div>

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value as HPCJobStatus | '')}
              className="px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            >
              <option value="">All Statuses</option>
              {Object.values(HPCJobStatus).map((status) => (
                <option key={status} value={status}>
                  {status.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>

          {/* Jobs List */}
          {hpcStore.jobLoading ? (
            <Card className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading jobs...</p>
            </Card>
          ) : hpcStore.jobs.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <div className="text-muted-foreground text-5xl mb-4">🖥️</div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No HPC Jobs Yet
                </h3>
                <p className="text-muted-foreground mb-4">
                  Create your first HPC job to start training on the cluster
                </p>
                <Button onClick={() => navigate('/hpc/jobs/new')}>
                  Create Job
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {hpcStore.jobs.map((job) => (
                <Card
                  key={job.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/hpc/jobs/${job.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-foreground">
                          {job.job_name}
                        </h3>
                        <Badge variant={getStatusBadgeVariant(job.status)}>
                          {job.status}
                        </Badge>
                        <Badge variant="gray">
                          {getJobTypeLabel(job.job_type)}
                        </Badge>
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground flex items-center gap-4">
                        <span>Cluster: {job.cluster_name}</span>
                        <span>Created: {formatDate(job.created_at)}</span>
                        {job.submitted_at && (
                          <span>Submitted: {formatDate(job.submitted_at)}</span>
                        )}
                      </div>
                    </div>

                    {/* Progress */}
                    {(job.status === HPCJobStatus.RUNNING || job.status === HPCJobStatus.CHECKPOINTING) && (
                      <div className="w-48">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{job.progress_percent.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all"
                            style={{ width: `${job.progress_percent}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {hpcStore.jobsPages > 1 && (
            <Pagination
              currentPage={hpcStore.jobsPage}
              totalPages={hpcStore.jobsPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Clusters Tab */}
      {activeTab === 'clusters' && (
        <div className="space-y-4">
          {hpcStore.clusterLoading ? (
            <Card className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading clusters...</p>
            </Card>
          ) : hpcStore.clusters.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <div className="text-muted-foreground text-5xl mb-4">🔌</div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No HPC Clusters Configured
                </h3>
                <p className="text-muted-foreground mb-4">
                  Add an HPC cluster to start submitting jobs
                </p>
                <Button onClick={() => setShowClusterModal(true)}>
                  Add Cluster
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hpcStore.clusters.map((cluster) => (
                <Card
                  key={cluster.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/hpc/clusters/${cluster.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-foreground">
                          {cluster.name}
                        </h3>
                        <Badge variant={getClusterStatusBadgeVariant(cluster.status)}>
                          {cluster.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {cluster.hostname}:{cluster.port}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Scheduler: {cluster.scheduler_type.toUpperCase()}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary-600">
                        {cluster.active_jobs_count}
                      </div>
                      <div className="text-xs text-muted-foreground">Active Jobs</div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        GPUs: {cluster.gpu_types.join(', ') || 'None'}
                      </span>
                      <span className="text-muted-foreground">
                        Queues: {cluster.available_queues.length}
                      </span>
                    </div>
                    {cluster.last_health_check && (
                      <div className="text-xs text-muted-foreground mt-2">
                        Last check: {formatDate(cluster.last_health_check)}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Interactive Sessions Tab */}
      {activeTab === 'interactive' && (
        <div className="space-y-6">
          {/* Introduction */}
          <Card>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-500/15 rounded-lg">
                  <CommandLineIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">
                    Interactive Sessions on Imperial CX3
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Start interactive GPU/CPU sessions directly from your browser or generate commands to run in your local terminal.
                  </p>
                </div>
              </div>
              <Button onClick={() => startTerminalSession('')}>
                <ComputerDesktopIcon className="w-4 h-4 mr-2" />
                Open Terminal
              </Button>
            </div>
          </Card>

          {/* Quick Configs */}
          <div>
            <h4 className="text-md font-medium text-foreground mb-3">Quick Configurations</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {interactiveConfigs.map((config, idx) => (
                <Card
                  key={idx}
                  className="hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary-300"
                  onClick={() => generateInteractiveCommand(config)}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-muted rounded-lg">
                      {config.gpus > 0 ? (
                        <CpuChipIcon className="w-5 h-5 text-green-600" />
                      ) : (
                        <CpuChipIcon className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-foreground">{config.name}</h5>
                      <p className="text-xs text-muted-foreground mt-1">{config.description}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Badge variant="gray">{config.cpus_per_node} CPUs</Badge>
                        <Badge variant="gray">{config.memory_gb}GB RAM</Badge>
                        {config.gpus > 0 && (
                          <Badge variant="success">
                            {config.gpus}x {config.gpu_type || 'GPU'}
                          </Badge>
                        )}
                        <Badge variant="gray">{config.walltime_hours}h</Badge>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Custom Configuration */}
          <Card>
            <h4 className="text-md font-medium text-foreground mb-4">Custom Configuration</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Nodes</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={customConfig.nodes}
                  onChange={(e) => setCustomConfig({...customConfig, nodes: parseInt(e.target.value) || 1})}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">CPUs</label>
                <input
                  type="number"
                  min="1"
                  max="128"
                  value={customConfig.cpus}
                  onChange={(e) => setCustomConfig({...customConfig, cpus: parseInt(e.target.value) || 8})}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Memory (GB)</label>
                <input
                  type="number"
                  min="1"
                  max="512"
                  value={customConfig.memory_gb}
                  onChange={(e) => setCustomConfig({...customConfig, memory_gb: parseInt(e.target.value) || 32})}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">GPUs</label>
                <input
                  type="number"
                  min="0"
                  max="8"
                  value={customConfig.gpus}
                  onChange={(e) => setCustomConfig({...customConfig, gpus: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">GPU Type</label>
                <select
                  value={customConfig.gpu_type}
                  onChange={(e) => setCustomConfig({...customConfig, gpu_type: e.target.value})}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                >
                  <option value="">Any</option>
                  {gpuTypes.map((gpu) => (
                    <option key={gpu.type} value={gpu.type}>
                      {gpu.type} ({gpu.vram_gb}GB)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Walltime (h)</label>
                <input
                  type="number"
                  min="0.5"
                  max="72"
                  step="0.5"
                  value={customConfig.walltime_hours}
                  onChange={(e) => setCustomConfig({...customConfig, walltime_hours: parseFloat(e.target.value) || 4})}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={() => generateInteractiveCommand()} className="w-full">
                  Generate
                </Button>
              </div>
            </div>
          </Card>

          {/* Generated Command */}
          {generatedCommand && (
            <Card className="bg-background text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Generated Command</h4>
                  <code className="block text-green-400 font-mono text-sm break-all">
                    {generatedCommand}
                  </code>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copyToClipboard(generatedCommand)}
                  >
                    <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => startTerminalSession(generatedCommand)}
                  >
                    <ComputerDesktopIcon className="w-4 h-4 mr-1" />
                    Start in Browser
                  </Button>
                </div>
              </div>
              {commandInstructions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h5 className="text-sm font-medium text-muted-foreground mb-2">Instructions</h5>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {commandInstructions.map((instruction, idx) => (
                      <li key={idx}>{instruction}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}

          {/* GPU Reference */}
          <Card>
            <h4 className="text-md font-medium text-foreground mb-4">Available GPUs at Imperial CX3</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">VRAM</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Availability</th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-gray-200">
                  {gpuTypes.map((gpu) => (
                    <tr key={gpu.type}>
                      <td className="px-4 py-2 text-sm font-medium text-foreground">{gpu.type}</td>
                      <td className="px-4 py-2 text-sm text-muted-foreground">{gpu.vram_gb} GB</td>
                      <td className="px-4 py-2 text-sm text-muted-foreground">{gpu.description}</td>
                      <td className="px-4 py-2">
                        <Badge
                          variant={
                            gpu.availability === 'excellent' ? 'success' :
                            gpu.availability === 'good' ? 'success' :
                            gpu.availability === 'moderate' ? 'warning' :
                            'error'
                          }
                        >
                          {gpu.availability}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Error Display */}
      {(hpcStore.clusterError || hpcStore.jobError) && (
        <Card className="bg-red-500/10 border-red-500/20">
          <div className="flex items-center justify-between">
            <p className="text-red-600">
              {hpcStore.clusterError || hpcStore.jobError}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => hpcStore.clearErrors()}
            >
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {/* Cluster Creation Modal */}
      {showClusterModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowClusterModal(false)}
            />

            {/* Modal */}
            <div className="relative bg-card rounded-lg shadow-xl max-w-2xl w-full p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">
                  Add HPC Cluster
                </h2>
                <button
                  onClick={() => setShowClusterModal(false)}
                  className="text-muted-foreground hover:text-muted-foreground"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Cluster Name *
                    </label>
                    <input
                      type="text"
                      value={newCluster.name}
                      onChange={(e) => setNewCluster({ ...newCluster, name: e.target.value })}
                      placeholder="e.g., Imperial CX3"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Scheduler Type
                    </label>
                    <select
                      value={newCluster.scheduler_type}
                      onChange={(e) => setNewCluster({ ...newCluster, scheduler_type: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="pbs">PBS Pro</option>
                      <option value="slurm">SLURM</option>
                      <option value="lsf">LSF</option>
                      <option value="sge">SGE</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newCluster.description}
                    onChange={(e) => setNewCluster({ ...newCluster, description: e.target.value })}
                    placeholder="e.g., Imperial College London HPC Cluster"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* Connection Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Hostname *
                    </label>
                    <input
                      type="text"
                      value={newCluster.hostname}
                      onChange={(e) => setNewCluster({ ...newCluster, hostname: e.target.value })}
                      placeholder="e.g., login.cx3.hpc.ic.ac.uk"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Port
                    </label>
                    <input
                      type="number"
                      value={newCluster.port}
                      onChange={(e) => setNewCluster({ ...newCluster, port: parseInt(e.target.value) || 22 })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* SSH Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      SSH Username *
                    </label>
                    <input
                      type="text"
                      value={newCluster.ssh_username}
                      onChange={(e) => setNewCluster({ ...newCluster, ssh_username: e.target.value })}
                      placeholder="Your HPC username"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Work Directory
                    </label>
                    <input
                      type="text"
                      value={newCluster.work_directory}
                      onChange={(e) => setNewCluster({ ...newCluster, work_directory: e.target.value })}
                      placeholder="e.g., /rds/general/user/username/home"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Queue and Job Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Default Queue
                    </label>
                    <input
                      type="text"
                      value={newCluster.default_queue}
                      onChange={(e) => setNewCluster({ ...newCluster, default_queue: e.target.value })}
                      placeholder="e.g., short"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Max Concurrent Jobs
                    </label>
                    <input
                      type="number"
                      value={newCluster.max_concurrent_jobs}
                      onChange={(e) => setNewCluster({ ...newCluster, max_concurrent_jobs: parseInt(e.target.value) || 10 })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Imperial CX3 Preset */}
                <div className="border-t pt-4">
                  <button
                    type="button"
                    onClick={() => setNewCluster({
                      ...newCluster,
                      name: 'Imperial CX3',
                      description: 'Imperial College London HPC Cluster (CX3)',
                      hostname: 'login.cx3.hpc.ic.ac.uk',
                      port: 22,
                      scheduler_type: 'pbs',
                      default_queue: 'short',
                      available_queues: ['short', 'medium', 'long', 'gpu', 'large'],
                      gpu_types: ['L40S', 'RTX6000', 'A100', 'V100', 'A40', 'RTX4090'],
                    })}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Load Imperial CX3 Preset
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button
                  variant="secondary"
                  onClick={() => setShowClusterModal(false)}
                  disabled={creatingCluster}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCluster}
                  disabled={creatingCluster}
                >
                  {creatingCluster ? 'Creating...' : 'Create Cluster'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Web Terminal Modal */}
      {showTerminal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex items-center justify-center min-h-screen p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-75"
              onClick={() => setShowTerminal(false)}
            />

            {/* Terminal Container */}
            <div className="relative w-full max-w-5xl h-[600px]">
              <WebTerminal
                hostname={terminalConfig.hostname}
                username={terminalConfig.username}
                initialCommand={terminalConfig.initialCommand}
                onClose={() => setShowTerminal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default HPCJobsPage
